import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import ReviewSlider from '../components/ReviewSlider';
import { imageUrl } from '../lib/images';

export default function HomePage() {
  const [activeFaq, setActiveFaq] = useState(null);
  const [treatmentSlide, setTreatmentSlide] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const [newsList, setNewsList] = useState([]);
  const [activeNewsModal, setActiveNewsModal] = useState(null);
  const [faqList, setFaqList] = useState(null);
  const [practitioners, setPractitioners] = useState(null);

  // Touch swipe support for mobile and iPad
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  useEffect(() => {
    const updateVisibleCount = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setVisibleCount(1);
      } else if (width < 1024) {
        setVisibleCount(2);
      } else {
        setVisibleCount(3);
      }
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  // Fetch news feed and filter entries older than 30 days
  useEffect(() => {
    fetch('/_api/public/v2/content/nyheder?sort=dato&order=desc')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.items) {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          const active = data.items
            .map((item) => ({
              ...item,
              title: item.titel || item.title,
              date: item.dato || item.date || item.createdAt,
              content: item.indhold || item.content,
              summary: item.opsummering || item.summary,
              badge: item.badge,
              image: item.billede || item.image,
            }))
            .filter((item) => {
              const itemDate = new Date(item.date);
              return !isNaN(itemDate.getTime()) && itemDate >= thirtyDaysAgo;
            });
          setNewsList(active);
        }
      })
      .catch((err) => console.error('Fejl ved indlæsning af nyheder:', err));
  }, []);

  // Fetch FAQ and practitioners from CMS
  useEffect(() => {
    fetch('/_api/public/v2/content/ofte_stillede_spoergsmaal?sort=sortering&order=asc')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.items && data.items.length > 0) {
          const clinicFaqs = data.items
            .filter((item) => !item.terms?.['faq-kategori']?.includes('h-jewlery-webshop'))
            .map((item) => ({
              q: item.spoergsmaal,
              a: item.svar,
            }));
          if (clinicFaqs.length > 0) setFaqList(clinicFaqs);
        }
      })
      .catch(() => {});

    fetch('/_api/public/v2/content/behandlere?sort=sortering&order=asc')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.items && data.items.length > 0) {
          setPractitioners(data.items);
        }
      })
      .catch(() => {});
  }, []);

  const treatments = [
    {
      id: 'fysiurgisk-massage',
      title: 'Fysiurgisk- & Sportsmassage (RAB)',
      icon: 'fa-hands',
      desc: 'Dybdegående massage og sportsmassage. RAB-godkendt (eneste kvalitetsstempel for massører) · Falck & AP Pension.',
      price: 'Fra 300 kr. · RAB-godkendt',
      link: '/behandlinger/fysiurgisk-massage',
    },
    {
      id: 'zoneterapi',
      title: 'Zoneterapi (RAB-godkendt)',
      icon: 'fa-shoe-prints',
      desc: 'Tryk under fødderne, der skaber balance. Tilskud fra ”Danmark” & sundhedsforsikring.',
      price: 'Fra 600 kr. (”Danmark” tilskud)',
      link: '/behandlinger/zoneterapi',
    },
    {
      id: 'ems-traening',
      title: 'EMS dragt (KUN dragt)',
      icon: 'fa-bolt',
      desc: 'Målrettet muskelstyrkelse og smertelindring. KUN dragt – ingen hård træning! 30 min. afsat.',
      price: '300 kr. / 30 min.',
      link: '/behandlinger/ems-traening',
    },
    {
      id: 'relax-massage',
      title: 'Relax massage',
      icon: 'fa-spa',
      desc: 'Afslappende wellness-massage med fokus på ro og velvære.',
      price: 'Fra 300 kr. (30 / 60 min)',
      link: '/behandlinger/relax-massage',
    },
    {
      id: 'voksbehandling',
      title: 'Voksbehandling, Bikini & Threading',
      icon: 'fa-feather',
      desc: 'Skånsom hårfjerning og bikini med rette voks. Bryn og overlæbe udføres med threading.',
      price: 'Ved kosmetolog Dianna',
      link: '/behandlinger/voksbehandling',
    },
    {
      id: 'pedicure-fodbehandling',
      title: 'Pedicure & fodbehandling',
      icon: 'fa-socks',
      desc: 'Omsorgsfuld fodpleje for bløde og sunde fødder.',
      price: 'Kosmetologisk fodpleje',
      link: '/behandlinger/pedicure-fodbehandling',
    },
    {
      id: 'specialiserede-teknikker',
      title: 'Specialiserede teknikker',
      icon: 'fa-circle-dot',
      desc: 'Målrettede teknikker som cupping, laser og akupunktur.',
      price: 'Målrettede teknikker',
      link: '/behandlinger/specialiserede-behandlinger',
    },
  ];

  const maxSlide = Math.max(0, treatments.length - visibleCount);

  const prevTreatment = () => {
    setTreatmentSlide((prev) => (prev > 0 ? prev - 1 : maxSlide));
  };

  const nextTreatment = () => {
    setTreatmentSlide((prev) => (prev < maxSlide ? prev + 1 : 0));
  };

  // Touch handlers for mobile and iPad
  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 40;
    if (distance > minSwipeDistance) {
      // Swiped left -> next
      nextTreatment();
    } else if (distance < -minSwipeDistance) {
      // Swiped right -> prev
      prevTreatment();
    }
  };

  const bookingUrl = 'https://djursmassage.app4.geckobooking.dk/site/booking.php?show=new_booking&icCode=69a506a6f06a0ca7022b880e8f6bcb65b11332&bId=11332&aDIfH=1&dTpl=1&sSToken=267bea05a53684a4a7d7ad2e6bae564a';
  const giftcardUrl = 'https://djursmassage.app4.geckobooking.dk/site/index.php?icCode=e6868204e7e11708974faa3b47f24d3db11332&dTpl=1';

  const faqItems = [
    {
      q: 'Hvorfor vælge massage?',
      a: (
        <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
          <p>Massage er meget mere end bare forkælelse – det er en investering i din fysiske og mentale sundhed.</p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              <strong>Lindrer spændinger og smerter:</strong> Massage hjælper med at løsne muskelspændinger, øge blodcirkulationen og reducere inflammation. Uanset om du døjer med nakkespændinger, rygsmerter eller overbelastede muskler, kan massage give effektiv lindring.
            </li>
            <li>
              <strong>Forebygger skader:</strong> Regelmæssig massage kan forbedre bevægelighed og fleksibilitet, og dermed forebygge overbelastningsskader – især for dem, der dyrker sport eller har fysisk krævende arbejde.
            </li>
            <li>
              <strong>Reducerer stress og forbedrer søvn:</strong> Massage aktiverer kroppens afslapningssystem og dæmper stresshormoner. Mange oplever bedre søvn, mindre uro og en følelse af ro – både i kroppen og hovedet.
            </li>
            <li>
              <strong>Styrker kroppens naturlige balance:</strong> Ved at stimulere lymfesystemet og blodcirkulationen støtter massage kroppens udrensning og helingsprocesser.
            </li>
          </ol>
        </div>
      ),
    },
    {
      q: 'Hvorfor skal jeg vælge zoneterapi?',
      a: (
        <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
          <p>
            Zoneterapi er en blid og naturlig behandlingsform, der støtter kroppens egen evne til at skabe balance og heling. Gennem tryk på bestemte zoner under fødderne, som afspejler kroppens organer og systemer, stimuleres blodcirkulationen, nervesystemet og energiflowet i kroppen.
          </p>
          <p>Mange vælger zoneterapi for at:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>reducere stress og spændinger</li>
            <li>lindre smerter i muskler og led</li>
            <li>støtte fordøjelsen og hormonbalancen</li>
            <li>styrke immunforsvaret</li>
            <li>opnå bedre søvn og mere overskud i hverdagen</li>
          </ul>
          <p>
            Zoneterapi ser ikke kun på symptomer, men på hele mennesket. Behandlingen tilpasses altid individuelt og tager udgangspunkt i netop dine behov og udfordringer. Mange oplever både dyb afslapning under behandlingen og en gradvis forbedring af deres velvære over tid.
          </p>
          <p>
            Zoneterapi kan bruges både forebyggende og som et supplement til anden behandling og er velegnet til både børn og voksne. Det er en tryg og naturlig måde at give kroppen de bedste betingelser for at komme i balance – indefra og ud.
          </p>
        </div>
      ),
    },
    {
      q: 'Hvad er prisen på en behandling?',
      a: (
        <p className="text-sm text-slate-700 leading-relaxed">
          Priserne starter <strong>fra 300 kr.</strong> (zoneterapi dog <strong>fra 600 kr.</strong>). Ved køb af klippekort opnår du desuden en attraktiv rabat på dine behandlinger.
        </p>
      ),
    },
    {
      q: 'Hvordan kan jeg booke en aftale?',
      a: (
        <p className="text-sm text-slate-700 leading-relaxed">
          Online booking - vælg et af stederne på hjemmesiden, hvor der står Book tid. Alternativt, ring på{' '}
          <a href="tel:28583907" className="font-semibold underline text-[#1E3A5F]">
            28 58 39 07
          </a>
          .
        </p>
      ),
    },
    {
      q: 'Hvordan betaler jeg?',
      a: (
        <p className="text-sm text-slate-700 leading-relaxed">
          Du kan betale med kontant, kort eller MobilePay. Du kan også købe et gavekort eller et klippekort online og betale med det.
        </p>
      ),
    },
  ];

  return (
    <>
      <SEOHead
        title="Djurs Massage & Zoneterapi | Klinik i Ryomgård"
        description="Fysiurgisk massage, RAB zoneterapi og effektiv smertelindring i Ryomgård. Mærk forskellen med over 24 års erfaring. Book din tid online hos Djurs Massage."
        ogImage="https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/djurs-massage-klinik-ryomgaard.jpg"
      />

      {/* Hero Section: Full-width herobillede med tekst og webform ovenpå */}
      <section className="relative overflow-hidden min-h-[640px] lg:min-h-[760px] flex items-center bg-[#041032]">
        {/* Full width background image with dark overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl('/_assets/hero-water-calm-wellness.jpg', 'width=1920,quality=90,format=auto')}
            alt="Klart og roligt vand - Djurs Massage og Zoneterapi"
            className="w-full h-full object-cover animate-gentle-zoom opacity-85"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#041032] via-[#041032]/60 to-[#041032]/70" />
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32 text-center">
          <div className="space-y-6 text-white flex flex-col items-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white font-serif leading-[1.15] max-w-3xl">
              Velkommen til Djurs Massage og Zoneterapi
            </h1>

            <div className="text-white text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
              <p>
                Professionel behandling med fokus på grundighed, nærvær og personlig lindring i Ryomgård.
              </p>
            </div>

            {/* CTAs: Centreret - med fjernet "Se vores behandlinger" */}
            <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#C9A770] text-[#041032] text-sm sm:text-base font-bold rounded-full hover:bg-[#d8b884] hover:shadow-lg transition-all whitespace-nowrap"
              >
                <i className="fa-solid fa-calendar-check text-[#041032]" aria-hidden="true" />
                <span>Book tid nu</span>
              </a>

              <a
                href={giftcardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-white hover:text-white transition-colors py-3 px-5 border border-white/40 hover:border-white rounded-full"
              >
                <i className="fa-solid fa-gift text-white" aria-hidden="true" />
                <span>Køb gavekort online</span>
              </a>
            </div>

            {/* Quick factual trust metrics - col-12 på mobil */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 pt-6 mt-3 w-full max-w-2xl mx-auto border-t border-white">
              <div className="border-l-2 border-[#C9A770] pl-3 py-1 text-left sm:text-center sm:border-l-0 sm:pt-1 sm:pl-0">
                <div className="text-lg sm:text-xl font-bold text-white">Fra 300 kr.</div>
                <div className="text-xs text-white/90">Fra 300 kr. · Rabat ved klippekort</div>
              </div>
              <div className="border-l-2 border-[#C9A770] pl-3 py-1 text-left sm:text-center sm:border-l-0 sm:pt-1 sm:pl-0">
                <div className="text-lg sm:text-xl font-bold text-white">Fra 2001</div>
                <div className="text-xs text-white/90">Uddannet behandler</div>
              </div>
              <div className="border-l-2 border-[#C9A770] pl-3 py-1 text-left sm:text-center sm:border-l-0 sm:pt-1 sm:pl-0">
                <div className="text-lg sm:text-xl font-bold text-white">RAB</div>
                <div className="text-xs text-white/90">Falck, AP & ”Danmark”</div>
              </div>
              <div className="border-l-2 border-[#C9A770] pl-3 py-1 text-left sm:text-center sm:border-l-0 sm:pt-1 sm:pl-0">
                <div className="text-lg sm:text-xl font-bold text-white">Ryomgård</div>
                <div className="text-xs text-white/90">Poppelvej 52</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Behandlingsoversigt (Direkte under Hero) - Karussel med pile for at se flere */}
      <section className="py-12 lg:py-16 bg-[#F3F4F6] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1E3A5F] font-serif">
                Hvad tilbyder Djurs Massage?
              </h2>
              <p className="text-slate-700 text-sm sm:text-base mt-2 max-w-2xl leading-relaxed">
                Vi tilbyder et bredt spektrum af målrettede behandlinger tilpasset dine smerter, spændinger eller behov for ro.
              </p>
            </div>

            {/* Navigation Arrows for Carousel - rene pile uden firkantboks */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={prevTreatment}
                aria-label="Forrige behandling"
                className="w-10 h-10 flex items-center justify-center text-[#1E3A5F] hover:text-black hover:scale-110 transition-all cursor-pointer bg-transparent border-0"
              >
                <i className="fa-solid fa-arrow-left text-lg" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={nextTreatment}
                aria-label="Næste behandling"
                className="w-10 h-10 flex items-center justify-center text-[#1E3A5F] hover:text-black hover:scale-110 transition-all cursor-pointer bg-transparent border-0"
              >
                <i className="fa-solid fa-arrow-right text-lg" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Carousel container with touch swipe for phones and iPads */}
          <div
            className="relative overflow-hidden cursor-grab active:cursor-grabbing select-none"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{
                transform: `translateX(-${treatmentSlide * (100 / visibleCount)}%)`,
              }}
            >
              {treatments.map((treatment) => (
                <div
                  key={treatment.id}
                  className="w-full sm:w-1/2 lg:w-1/3 shrink-0 p-2 sm:p-3"
                >
                  <div className="h-full bg-white p-6 sm:p-7 shadow-md hover:shadow-xl transition-shadow flex flex-col justify-between group">
                    <div>
                      <div className="text-[#C9A770] text-2xl mb-4">
                        <i className={`fa-solid ${treatment.icon}`} aria-hidden="true" />
                      </div>
                      <h3 className="text-xl font-bold text-[#1E3A5F] group-hover:text-black transition-colors font-serif">
                        {treatment.title}
                      </h3>
                      <p className="text-slate-700 group-hover:text-black transition-colors text-sm mt-2 leading-relaxed">
                        {treatment.desc}
                      </p>
                    </div>

                    <div className="pt-4 mt-6 border-t border-slate-100 flex items-center justify-between text-xs">
                      <span className="font-bold text-[#1E3A5F] group-hover:text-black">{treatment.price}</span>
                      <Link to={treatment.link} className="font-bold text-[#1E3A5F] hover:text-black">
                        Læs mere →
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Swipe indicator and Dots Navigation */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-600 lg:hidden bg-white/70 px-3 py-1.5 rounded-full border border-slate-200">
              <i className="fa-solid fa-arrows-left-right text-[#C9A770]" aria-hidden="true" />
              <span>Swipe mod højre/venstre på telefon & tablet</span>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center items-center gap-2 mx-auto sm:mx-0 sm:ml-auto">
              {Array.from({ length: maxSlide + 1 }).map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setTreatmentSlide(idx)}
                  className={`h-2 transition-all ${
                    treatmentSlide === idx ? 'w-8 bg-[#041032]' : 'w-2 bg-[#041032]/30'
                  }`}
                  aria-label={`Gå til slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Aktuelt & Nyheder (Filtreret: kun opslag fra de seneste 30 dage) */}
      {newsList.length > 0 && (
        <section className="py-12 lg:py-16 bg-white border-b border-[#E8E1D5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs font-bold uppercase tracking-wider rounded-full mb-2">
                  <i className="fa-solid fa-bullhorn text-[#C9A770]" aria-hidden="true" />
                  <span>Klinikkens Nyhedsfeed</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1E3A5F] font-serif">
                  Aktuelt & Nyheder
                </h2>
                <p className="text-slate-700 text-sm sm:text-base mt-2 max-w-2xl leading-relaxed">
                  Hold dig opdateret med de seneste nyheder, nye behandlinger og vigtige informationer fra Djurs Massage.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {newsList.map((post) => {
                const dateObj = new Date(post.date || post.createdAt);
                const formattedDate = !isNaN(dateObj.getTime())
                  ? dateObj.toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })
                  : '';
                const postImage = post.image?.[0];

                return (
                  <article
                    key={post.id}
                    className="bg-[#FAF9F6] border border-[#E8E1D5] hover:shadow-lg transition-all duration-200 flex flex-col justify-between overflow-hidden group"
                  >
                    <div>
                      {postImage && (
                        <div className="aspect-16/9 overflow-hidden bg-slate-100">
                          <img
                            src={imageUrl(postImage, 'width=600,height=340,fit=cover,quality=85,format=auto')}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <div className="p-5 sm:p-6">
                        <div className="flex items-center justify-between gap-2 mb-3">
                          {post.badge && (
                            <span className="px-2.5 py-0.5 bg-[#C9A770]/20 text-[#1E3A5F] text-xs font-bold uppercase tracking-wider">
                              {post.badge}
                            </span>
                          )}
                          {formattedDate && (
                            <span className="text-xs text-slate-500 font-medium ml-auto flex items-center gap-1">
                              <i className="fa-regular fa-clock" aria-hidden="true" />
                              <span>{formattedDate}</span>
                            </span>
                          )}
                        </div>

                        <h3 className="text-lg sm:text-xl font-bold text-[#1E3A5F] group-hover:text-black transition-colors font-serif leading-snug">
                          {post.title}
                        </h3>

                        {post.summary && (
                          <p className="text-slate-700 text-sm mt-2.5 line-clamp-3 leading-relaxed">
                            {post.summary}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="p-5 sm:p-6 pt-0">
                      <button
                        type="button"
                        onClick={() => setActiveNewsModal(post)}
                        className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-[#1E3A5F] hover:text-black group-hover:underline cursor-pointer"
                      >
                        <span>Læs hele opslaget</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Nyhedsmodal til fuld læsning af opslag */}
      {activeNewsModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setActiveNewsModal(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white max-w-2xl w-full p-6 sm:p-8 rounded-lg shadow-2xl relative my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveNewsModal(null)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors cursor-pointer"
              aria-label="Luk opslag"
            >
              <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
            </button>

            {activeNewsModal.badge && (
              <span className="inline-block px-3 py-0.5 bg-[#C9A770]/20 text-[#1E3A5F] text-xs font-bold uppercase tracking-wider mb-2">
                {activeNewsModal.badge}
              </span>
            )}

            <h3 className="text-2xl sm:text-3xl font-bold text-[#1E3A5F] font-serif pr-8">
              {activeNewsModal.title}
            </h3>

            {activeNewsModal.date && (
              <p className="text-xs text-slate-500 mt-1 mb-4 flex items-center gap-1.5">
                <i className="fa-regular fa-calendar-check" aria-hidden="true" />
                <span>
                  Udgivet den{' '}
                  {new Date(activeNewsModal.date).toLocaleDateString('da-DK', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              </p>
            )}

            {activeNewsModal.image?.[0] && (
              <div className="aspect-16/9 overflow-hidden rounded-md my-4">
                <img
                  src={imageUrl(activeNewsModal.image[0], 'width=900,height=500,fit=cover,quality=90,format=auto')}
                  alt={activeNewsModal.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div
              className="prose prose-slate max-w-none text-slate-700 text-sm sm:text-base leading-relaxed space-y-3 mt-4"
              dangerouslySetInnerHTML={{ __html: activeNewsModal.content }}
            />

            <div className="mt-8 pt-4 border-t border-slate-200 flex justify-end">
              <button
                type="button"
                onClick={() => setActiveNewsModal(null)}
                className="px-5 py-2.5 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:bg-black transition-colors cursor-pointer"
              >
                Luk opslag
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sektion: Mød de 3 behandlere (NO paragraph above heading, NO box behind icon, NO text on images) */}
      <section className="py-12 lg:py-16 bg-white border-b border-[#E8E1D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1E3A5F] font-serif">
              Mød de 3 behandlere hos Djurs Massage
            </h2>
            <p className="text-slate-700 text-sm sm:text-base mt-2 leading-relaxed">
              Hos Djurs Massage møder du engagerede behandlere, der arbejder med stor grundighed og dyb respekt for din krop. Hver behandler har sit specifikke fagområde, så du altid modtager den rette faglige ekspertise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Behandler 1: Tine Thorsen, indehaver */}
            <div className="group bg-white border border-[#E8E1D5] hover:shadow-lg transition-shadow flex flex-col justify-between overflow-hidden">
              <div>
                <div className="aspect-4/5 overflow-hidden bg-[#E8E1D5]">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/tine-vium-thorsen-fysiurgisk-massaor.png', 'width=600,height=750,fit=cover,quality=90,format=auto')}
                    alt="Tine Thorsen, indehaver - RAB-godkendt massør hos Djurs Massage"
                    className="w-full h-full object-cover grayscale-15 group-hover:grayscale-0 transition-all duration-300"
                  />
                </div>
                <div className="p-6 pb-2">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1E3A5F] group-hover:text-black transition-colors font-serif mt-1">
                    Tine Thorsen, indehaver
                  </h3>
                  <div className="text-xs font-bold text-slate-800 mt-1">
                    Fysiurgisk massør • RAB-godkendt • 25 års erfaring
                  </div>
                  <p className="text-slate-700 group-hover:text-black transition-colors text-sm mt-3 leading-relaxed">
                    Etablerede Djurs Massage for over 24 år siden. Tine er RAB-godkendt (det eneste officielle kvalitetsstempel for massører i Danmark) og godkendt ved Falck Healthcare & AP Pension.
                  </p>
                </div>
              </div>

              <div className="p-6 pt-4 border-t border-[#E8E1D5] flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">Fysiurgisk massage</span>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1E3A5F] hover:text-black transition-colors"
                >
                  <span>Book tid hos Tine</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* Behandler 2: Dianna - Ingen padding omkring billede */}
            <div className="group bg-white border border-[#E8E1D5] hover:shadow-lg transition-shadow flex flex-col justify-between overflow-hidden">
              <div>
                <div className="aspect-4/5 overflow-hidden bg-[#E8E1D5]">
                  <img
                    src={imageUrl('/_assets/portrait-dianna-kosmetolog.jpg', 'width=600,height=750,fit=cover,quality=90,format=auto')}
                    alt="Dianna - Kosmetolog hos Djurs Massage"
                    className="w-full h-full object-cover grayscale-15 group-hover:grayscale-0 transition-all duration-300"
                  />
                </div>
                <div className="p-6 pb-2">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1E3A5F] group-hover:text-black transition-colors font-serif mt-1">
                    Dianna
                  </h3>
                  <div className="text-xs font-bold text-slate-800 mt-1">
                    Uddannet kosmetolog
                  </div>
                  <p className="text-slate-700 group-hover:text-black transition-colors text-sm mt-3 leading-relaxed">
                    Uddannet kosmetolog med ekspertise i afslappende kropsmassage, ansigtsbehandlinger, voks og skånsom pedicure.
                  </p>
                </div>
              </div>

              <div className="p-6 pt-4 border-t border-[#E8E1D5] flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">Kosmetologisk pleje</span>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1E3A5F] hover:text-black transition-colors"
                >
                  <span>Book tid hos Dianna</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* Behandler 3: Sophie - Ingen padding omkring billede */}
            <div className="group bg-white border border-[#E8E1D5] hover:shadow-lg transition-shadow flex flex-col justify-between overflow-hidden">
              <div>
                <div className="aspect-4/5 overflow-hidden bg-[#E8E1D5]">
                  <img
                    src="https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/1000004057.jpg"
                    alt="Sophie Hebsgaard Ede - Zoneterapeut hos Djurs Massage"
                    className="w-full h-full object-cover grayscale-15 group-hover:grayscale-0 transition-all duration-300"
                  />
                </div>
                <div className="p-6 pb-2">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1E3A5F] group-hover:text-black transition-colors font-serif mt-1">
                    Sophie
                  </h3>
                  <div className="text-xs font-bold text-slate-800 mt-1">
                    RAB-Zoneterapi (voksne & babyer)
                  </div>
                  <p className="text-slate-700 group-hover:text-black transition-colors text-sm mt-3 leading-relaxed">
                    RAB-godkendt zoneterapeut med tilskud fra bl.a. Sygeforsikring Danmark. Målrettet reflekszonebehandling – også til babyer.
                  </p>
                </div>
              </div>

              <div className="p-6 pt-4 border-t border-[#E8E1D5] flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">Zoneterapi</span>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1E3A5F] hover:text-black transition-colors"
                >
                  <span>Book tid hos Sophie</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Fysiurgisk massage og zoneterapi i Ryomgård (SEO-sektion) */}
      <section className="py-14 lg:py-20 bg-[#F3F4F6] border-b border-[#E8E1D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top: Hovedoverskrift, intro, fysiurgisk massage sektion + billeder */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#C9A770]/20 text-[#1E3A5F] text-xs font-bold uppercase tracking-wider rounded-full">
                <i className="fa-solid fa-spa text-[#C9A770]" aria-hidden="true" />
                <span>Djurs Massage &amp; Zoneterapi i Ryomgård</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1E3A5F] font-serif leading-tight">
                Fysiurgisk massage og zoneterapi i Ryomgård
              </h2>

              <p className="text-slate-700 text-base sm:text-lg leading-relaxed font-normal">
                Hos Djurs Massage får du fysiurgisk massage i Ryomgård med fokus på muskelspændinger og fysisk velvære. Måske mærker du ømhed i ryggen efter en lang arbejdsdag eller spændinger i nakke og skuldre. Vi hjælper dig med at vælge en behandling, der passer til dine behov og giver plads til en pause.
              </p>

              <div className="pt-4 border-t border-[#E8E1D5] space-y-4">
                <h3 className="text-xl sm:text-2xl font-bold text-[#1E3A5F] font-serif">
                  Fysiurgisk massage med blik for hele dig
                </h3>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  Tine har 25 års erfaring som RAB-godkendt massør og står for vores fysiurgiske massage. Vi ser på helheden og lytter til, hvordan du bruger din krop i hverdagen. Behandlingen tager udgangspunkt i de områder, som generer dig, frem for at følge det samme forløb hver gang.
                </p>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  Du kan vælge en behandling med fokus på ryg, nakke og skuldre eller en helkropsmassage. Tryk og greb tilpasses undervejs, så fortæl gerne, hvordan massagen føles. Målet er at arbejde med dine spændinger i et tempo og med en intensitet, der passer til dig. Du behøver ikke ønske en hård massage for at vælge denne behandling.
                </p>
              </div>

              {/* Handlingsknapper */}
              <div className="pt-2 flex flex-wrap items-center gap-3">
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:shadow-md transition-shadow whitespace-nowrap"
                >
                  <i className="fa-solid fa-calendar-check text-[#C9A770]" aria-hidden="true" />
                  <span>Book tid til massage</span>
                </a>
                <Link
                  to="/behandlinger/fysiurgisk-massage"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-transparent hover:bg-transparent text-[#1E3A5F] hover:text-black border border-[#1E3A5F] text-xs sm:text-sm font-semibold rounded-full transition-colors whitespace-nowrap"
                >
                  <span>Læs om fysiurgisk massage</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Billedgitter der bevarer hjemmesidens visuelle udtryk */}
            <div className="lg:col-span-5 grid grid-cols-2 gap-4 lg:sticky lg:top-28">
              <div className="space-y-4">
                <div className="aspect-4/5 overflow-hidden border border-[#E8E1D5] bg-[#E8E1D5] shadow-xs">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/dybdegaaende-massage-behandling.jpg', 'width=600,height=750,fit=cover,quality=85,format=auto')}
                    alt="Grundig fysiurgisk rygmassage i klinikken"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square overflow-hidden border border-[#E8E1D5] bg-[#E8E1D5] shadow-xs">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/omsorgsfuld-behandling-haender-velvaere.jpeg', 'width=600,height=600,fit=cover,quality=85,format=auto')}
                    alt="Præcis berøring og manuel teknik"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-6">
                <div className="aspect-square overflow-hidden border border-[#E8E1D5] bg-[#E8E1D5] shadow-xs">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/zoneterapi-fodmassage-reflekspunkter.jpeg', 'width=600,height=750,fit=cover,quality=85,format=auto')}
                    alt="Præcisionszoneterapi med tryk på fodsål"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-4/5 overflow-hidden border border-[#E8E1D5] bg-[#E8E1D5] shadow-xs">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/cupping-massage-ryg-undertryk.jpg', 'width=600,height=600,fit=cover,quality=85,format=auto')}
                    alt="Cupping og kropsbehandling i klinikken"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Mellemstykke: 3 overskuelige emnekort (Hvad er fysiurgisk massage, Zoneterapi, Voksbehandling) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-12 pt-10 border-t border-[#E8E1D5]">
            {/* Kort 1: Hvad er fysiurgisk massage? */}
            <div className="bg-white border border-[#E8E1D5] p-6 sm:p-7 flex flex-col justify-between shadow-xs hover:border-[#C9A770]/60 transition-colors">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-full bg-[#1E3A5F]/5 flex items-center justify-center text-[#C9A770]">
                  <i className="fa-solid fa-hand-holding-heart text-lg" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#1E3A5F] font-serif leading-snug">
                  Hvad er fysiurgisk massage?
                </h3>
                <p className="text-slate-700 text-sm leading-relaxed">
                  Fysiurgisk massage er en massageform, hvor vi arbejder med muskler og blødt væv ud fra kroppens anatomi. Den adskiller sig fra relax massage, hvor afslapning er det primære fokus. Hos os kan du vælge 30, 45, 60 eller 90 minutter. Læs mere om fysiurgisk massage og mulighederne for at tilpasse din behandling.
                </p>
              </div>
              <div className="pt-4 border-t border-slate-100 mt-4">
                <Link
                  to="/behandlinger/fysiurgisk-massage"
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-[#1E3A5F] hover:text-[#C9A770] transition-colors"
                >
                  <span>Læs mere om fysiurgisk massage</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Kort 2: Zoneterapi og kropspleje til forskellige behov */}
            <div className="bg-white border border-[#E8E1D5] p-6 sm:p-7 flex flex-col justify-between shadow-xs hover:border-[#C9A770]/60 transition-colors">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-full bg-[#1E3A5F]/5 flex items-center justify-center text-[#C9A770]">
                  <i className="fa-solid fa-shoe-prints text-lg" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#1E3A5F] font-serif leading-snug">
                  Zoneterapi og kropspleje til forskellige behov
                </h3>
                <p className="text-slate-700 text-sm leading-relaxed">
                  I zoneterapi arbejder behandleren med tryk på bestemte områder af fødderne. Hos os udføres behandlingen af zoneterapeut Sophie Hebsgaard Ede. Den første konsultation giver tid til både samtale og behandling, så dine ønsker bliver hørt. Du kan læse mere om forløbet på vores side om zoneterapi.
                </p>
              </div>
              <div className="pt-4 border-t border-slate-100 mt-4">
                <Link
                  to="/behandlinger/zoneterapi"
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-[#1E3A5F] hover:text-[#C9A770] transition-colors"
                >
                  <span>Læs mere om zoneterapi</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Kort 3: Voksbehandling med omhu for din hud */}
            <div className="bg-white border border-[#E8E1D5] p-6 sm:p-7 flex flex-col justify-between shadow-xs hover:border-[#C9A770]/60 transition-colors">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-full bg-[#1E3A5F]/5 flex items-center justify-center text-[#C9A770]">
                  <i className="fa-solid fa-feather-pointed text-lg" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#1E3A5F] font-serif leading-snug">
                  Voksbehandling med omhu for din hud
                </h3>
                <p className="text-slate-700 text-sm leading-relaxed">
                  Vores voksbehandling er kosmetologisk hårfjerning på blandt andet ben, arme, ryg, overlæbe og bryn. Behandlingen varer 15 til 45 minutter afhængigt af området. Læs om voksbehandling, eller kontakt os, hvis du har spørgsmål til din hud og det område, du ønsker behandlet.
                </p>
              </div>
              <div className="pt-4 border-t border-slate-100 mt-4 flex items-center justify-between gap-3">
                <Link
                  to="/behandlinger/voksbehandling"
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-[#1E3A5F] hover:text-[#C9A770] transition-colors"
                >
                  <span>Læs om voksbehandling</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
                <Link
                  to="/kontakt"
                  className="text-xs text-slate-500 hover:text-[#1E3A5F] underline"
                >
                  Kontakt os
                </Link>
              </div>
            </div>
          </div>

          {/* Bund: Find tid til massage i Ryomgård fremhævet blok */}
          <div className="mt-8 bg-white border border-[#E8E1D5] p-6 sm:p-8 lg:p-10 shadow-xs relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1E3A5F] via-[#C9A770] to-[#1E3A5F]" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8 space-y-3.5">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#C9A770]">
                  <i className="fa-solid fa-location-dot" aria-hidden="true" />
                  <span>Klinikken i Ryomgård &bull; Poppelvej 52</span>
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#1E3A5F] font-serif">
                  Find tid til massage i Ryomgård
                </h3>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  Klinikken ligger på Poppelvej 52 i Ryomgård. Vi tager imod kunder fra Rønde, Grenaa, Midtdjurs og resten af Djursland, men alle behandlinger foregår i klinikken her i Ryomgård. Kom gerne nogle minutter før din aftale. Efter behandlingen kan du betale med MobilePay eller kontant.
                </p>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  Er du i tvivl om, hvilken behandling du skal vælge, hjælper vi dig gerne videre. Fortæl os, hvor du oplever spændinger, eller om du mest har brug for afslapning. Kontakt Djurs Massage for at høre om mulighederne og få hjælp til at bestille tid til fysiurgisk massage.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center">
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#1E3A5F] text-white text-sm font-semibold rounded-full hover:bg-black transition-colors shadow-sm text-center whitespace-nowrap"
                >
                  <i className="fa-solid fa-calendar-check text-[#C9A770]" aria-hidden="true" />
                  <span>Bestil tid online</span>
                </a>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-transparent text-[#1E3A5F] hover:text-black border border-[#1E3A5F] text-sm font-semibold rounded-full transition-colors text-center whitespace-nowrap"
                >
                  <i className="fa-solid fa-envelope text-[#C9A770]" aria-hidden="true" />
                  <span>Kontakt klinikken</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: EMS Dragt hos Djurs Massage */}
      <section className="py-14 lg:py-20 bg-white border-b border-[#E8E1D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Billede venstre kolonne */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative">
                <div className="aspect-4/3 sm:aspect-16/10 rounded-lg overflow-hidden border-2 border-[#E8E1D5] shadow-lg bg-[#FAF9F6]">
                  <img
                    src={imageUrl('/_assets/ems-traening-dragt-klinik.jpg', 'width=800,height=600,fit=cover,quality=90,format=auto')}
                    alt="EMS dragt hos Djurs Massage i Ryomgård"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-[#041032] text-white p-4 rounded-md shadow-xl hidden sm:block max-w-xs border-l-4 border-[#C9A770]">
                  <div className="text-xl font-bold font-serif text-[#C9A770]">KUN dragt</div>
                  <div className="text-xs text-white mt-0.5">Ingen træning – styrker muskler og lindrer smerter</div>
                </div>
              </div>
            </div>

            {/* Tekst højre kolonne */}
            <div className="lg:col-span-7 space-y-6 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#C9A770]/20 text-[#1E3A5F] text-xs font-bold uppercase tracking-wider rounded-full">
                <i className="fa-solid fa-bolt text-[#C9A770]" aria-hidden="true" />
                <span>EMS Dragt (Ingen Træning)</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1E3A5F] font-serif leading-tight">
                EMS dragt hos Djurs Massage
              </h2>

              <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                Bemærk: Hos os er det <strong>KUN dragt – ingen hård træning</strong>. Vores avancerede EMS-dragt (Elektro-Myo-Stimulation) koster <strong>300 kr. for 30 minutter</strong> (da folk skal i og ud af dragt samt rengøring). Vi tilbyder ikke gratis prøvetræning. Dragten kan anvendes alene for målrettet at styrke musklerne, eller i effektiv kombination med massage. Dragten aktiverer kroppens egne smertehæmmende stoffer, hvilket giver en markant smertelindrende effekt.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                <div className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-[#C9A770] text-sm mt-1 shrink-0" aria-hidden="true" />
                  <span className="text-xs sm:text-sm text-slate-800 font-medium"><strong>Styrker muskler skånsomt:</strong> Ingen tunge vægte eller led-slitage.</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-[#C9A770] text-sm mt-1 shrink-0" aria-hidden="true" />
                  <span className="text-xs sm:text-sm text-slate-800 font-medium"><strong>Smertelindrende:</strong> Frigør kroppens smertehæmmende stoffer.</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-[#C9A770] text-sm mt-1 shrink-0" aria-hidden="true" />
                  <span className="text-xs sm:text-sm text-slate-800 font-medium"><strong>Fleksibel brug:</strong> Alene eller kombineret med massage.</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-[#C9A770] text-sm mt-1 shrink-0" aria-hidden="true" />
                  <span className="text-xs sm:text-sm text-slate-800 font-medium"><strong>Aflaster leddene:</strong> Muskler og sener slipper deres spænding.</span>
                </div>
              </div>

              <div className="pt-3 flex flex-wrap items-center gap-3">
                <Link
                  to="/behandlinger/ems-traening"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:bg-black transition-colors shadow-xs"
                >
                  <span>Læs mere om EMS dragt</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-transparent text-[#1E3A5F] hover:text-black border border-[#1E3A5F] text-xs sm:text-sm font-semibold rounded-full transition-colors"
                >
                  <i className="fa-solid fa-calendar-check text-[#C9A770]" aria-hidden="true" />
                  <span>Book tid til EMS dragt</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Sektion: Ofte stillede spørgsmål (FAQ) - Mørkeblå sektion (#041032) */}
      <section className="py-14 lg:py-20 bg-[#041032] border-b border-[#0d225d] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
            {/* Venstre kolonne: Overskrift & hjælpetekst */}
            <div className="lg:col-span-4 lg:sticky lg:top-36 h-fit">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-serif leading-tight">
                Ofte stillede spørgsmål
              </h2>
              <p className="text-white text-sm sm:text-base mt-4 leading-relaxed">
                Her finder du svar på de mest almindelige spørgsmål om vores behandlinger, tilskud, priser og tidsbestilling.
              </p>
              <div className="mt-8 pt-6 border-t border-white/15 hidden lg:block">
                <p className="text-xs text-[#C9A770] uppercase tracking-wider font-semibold">
                  Foretrækker du skriftlig kontakt?
                </p>
                <p className="text-sm text-white mt-1">
                  Da vi ofte er optaget af behandlinger, foretrækkes skriftlige henvendelser via kontaktformularen eller SMS til{' '}
                  <a href="tel:28583907" className="font-bold text-white underline hover:text-[#C9A770] transition-colors whitespace-nowrap">
                    28 58 39 07
                  </a>. Du kan også benytte vores venteliste i bookingsystemet, hvis der ikke er en ledig tid.
                </p>
              </div>
            </div>

            {/* Højre kolonne: Moderne og ren accordion */}
            <div className="lg:col-span-8 divide-y divide-white/15">
              {(faqList || faqItems).map((item, index) => {
                const isOpen = activeFaq === index;
                return (
                  <div key={index} className="py-5 sm:py-6 first:pt-0 last:pb-0">
                    <button
                      type="button"
                      onClick={() => setActiveFaq(isOpen ? null : index)}
                      className="w-full text-left flex items-start justify-between gap-4 font-bold text-white hover:text-[#C9A770] text-base sm:text-lg font-serif transition-colors cursor-pointer group"
                    >
                      <span className="group-hover:text-[#C9A770] transition-colors">{item.q}</span>
                      <span className="shrink-0 mt-1 w-6 h-6 flex items-center justify-center text-white group-hover:text-[#C9A770] transition-colors">
                        <i
                          className={`fa-solid ${
                            isOpen ? 'fa-minus text-[#C9A770]' : 'fa-plus'
                          } text-sm transition-transform duration-200`}
                          aria-hidden="true"
                        />
                      </span>
                    </button>
                    {isOpen && (
                      <div className="pt-4 text-white leading-relaxed text-sm sm:text-base pr-8 animate-fadeIn">
                        {typeof item.a === 'string' ? (
                          <div
                            className="space-y-2 [&>p]:leading-relaxed [&>ul]:list-disc [&>ul]:pl-5 [&>ol]:list-decimal [&>ol]:pl-5 [&>li]:mt-1 [&_strong]:text-white"
                            dangerouslySetInnerHTML={{ __html: item.a }}
                          />
                        ) : (
                          item.a
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Anmeldelser (ReviewSlider) */}
      <section className="py-12 lg:py-16 bg-white border-b border-[#E8E1D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ReviewSlider />
        </div>
      </section>

      {/* Sektion: Beliggenhed & Klinikinfo (NO box behind icons, NO paragraph above heading) */}
      <section className="py-12 lg:py-16 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 space-y-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1E3A5F] font-serif">
                Besøg klinikken i Ryomgård
              </h2>
              <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                Djurs Massage har adresse i rolige omgivelser på Poppelvej 52 i Ryomgård. Her er gode parkeringsmuligheder og rolige rammer, hvor du kan koble fra og fokusere helt på din behandling.
              </p>

              <div className="space-y-4 text-sm text-slate-700">
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-location-dot text-[#C9A770] text-base mt-1 shrink-0" aria-hidden="true" />
                  <div>
                    <div className="font-bold text-[#1E3A5F]">Djurs Massage</div>
                    <div>Poppelvej 52, 8550 Ryomgård</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-phone text-[#C9A770] text-base shrink-0" aria-hidden="true" />
                  <div>
                    <a
                      href="tel:28583907"
                      className="font-bold text-[#1E3A5F] hover:text-black transition-colors whitespace-nowrap"
                    >
                      28 58 39 07
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-envelope text-[#C9A770] text-base shrink-0" aria-hidden="true" />
                  <div>
                    <a
                      href="mailto:djursmassage@gmail.com"
                      className="font-medium text-[#1E3A5F] hover:text-black transition-colors whitespace-nowrap"
                    >
                      djursmassage@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <i className="fa-brands fa-facebook-f text-[#C9A770] text-base shrink-0" aria-hidden="true" />
                  <div>
                    <a
                      href="https://facebook.com/djursmassage.dk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-[#1E3A5F] hover:text-black transition-colors"
                    >
                      facebook.com/djursmassage.dk
                    </a>
                  </div>
                </div>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-6">
                <a
                  href="https://maps.google.com/maps?q=Poppelvej%2052%2C%208550%20Ryomg%C3%A5rd%2C%20Danmark"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-[#1E3A5F] hover:text-black transition-colors"
                >
                  <i className="fa-solid fa-map-location-dot text-[#C9A770]" aria-hidden="true" />
                  <span>Åbn i Google Maps</span>
                </a>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-3 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:shadow-md transition-shadow"
                >
                  <i className="fa-solid fa-calendar-check text-[#C9A770]" aria-hidden="true" />
                  <span>Book tid</span>
                </a>
              </div>
            </div>

            {/* Right: Google Maps Iframe */}
            <div className="lg:col-span-6 border border-[#E8E1D5] overflow-hidden aspect-4/3 bg-[#E8E1D5] shadow-sm">
              <iframe
                title="Djurs Massage lokation på Google Maps"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2215.342111005232!2d10.499696977239324!3d56.381389443896564!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464c39e24672bb19%3A0xe54d8ec5b1f7fafe!2sPoppelvej%2052%2C%208550%20Ryomg%C3%A5rd!5e0!3m2!1sda!2sdk!4v1710000000000!5m2!1sda!2sdk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full min-h-[320px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
